
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowUpRight, CalendarDays, GiftIcon } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { formatDistanceToNow } from "date-fns";

export type CampaignStatus = "active" | "scheduled" | "ended" | "draft";

export interface CampaignCardProps {
  id: string;
  title: string;
  description: string;
  status: CampaignStatus;
  vouchersClaimed: number;
  vouchersTotal: number;
  startDate: Date;
  endDate: Date;
  onClick?: () => void;
}

export function CampaignCard({
  title,
  description,
  status,
  vouchersClaimed,
  vouchersTotal,
  startDate,
  endDate,
  onClick,
}: CampaignCardProps) {
  const statusColor: Record<CampaignStatus, string> = {
    active: "bg-green-100 text-green-800 dark:bg-green-800/20 dark:text-green-400",
    scheduled: "bg-blue-100 text-blue-800 dark:bg-blue-800/20 dark:text-blue-400",
    ended: "bg-gray-100 text-gray-800 dark:bg-gray-800/20 dark:text-gray-400",
    draft: "bg-amber-100 text-amber-800 dark:bg-amber-800/20 dark:text-amber-400",
  };

  const progress = (vouchersClaimed / vouchersTotal) * 100;
  const timeRemaining = formatDistanceToNow(endDate, { addSuffix: true });

  return (
    <Card className="overflow-hidden hover:shadow-md transition-shadow">
      <CardHeader className="pb-2">
        <div className="flex justify-between items-start">
          <CardTitle className="text-lg font-semibold">{title}</CardTitle>
          <Badge className={statusColor[status]}>{status}</Badge>
        </div>
        <CardDescription className="line-clamp-2 mt-1">{description}</CardDescription>
      </CardHeader>
      <CardContent className="pb-2">
        <div className="flex items-center text-sm text-muted-foreground mb-3">
          <CalendarDays size={14} className="mr-1" />
          <span>
            {startDate.toLocaleDateString()} - {endDate.toLocaleDateString()}
          </span>
          <Badge variant="outline" className="ml-auto text-xs">
            Ends {timeRemaining}
          </Badge>
        </div>
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="flex items-center">
              <GiftIcon size={14} className="mr-1" />
              Vouchers Claimed
            </span>
            <span className="font-medium">
              {vouchersClaimed}/{vouchersTotal}
            </span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>
      </CardContent>
      <CardFooter>
        <Button variant="ghost" size="sm" className="ml-auto flex items-center gap-1" onClick={onClick}>
          View Details
          <ArrowUpRight size={16} />
        </Button>
      </CardFooter>
    </Card>
  );
}
